import type { AdminRuleVersionsResponse, AdminRulesGetResponse, RuleSet } from '@kkp-fraud/shared';
import bundledRules from '../../../../rules/rules.v1.json';
import type { RuleSetList, RuleSetView } from '../ruleSets';

/**
 * 2c rule sets — two example sets for `VITE_DEMO=1`, after the real ones. Every id starts with
 * `demo-`; they are read-only (the screen offers no write on them, the pickers open them
 * read-only) and their documents are built from `rules/rules.v1.json`, never fetched.
 */

export const DEMO_RULE_SET_PREFIX = 'demo-';

export function isDemoRuleSetId(id: string | undefined): boolean {
  return typeof id === 'string' && id.startsWith(DEMO_RULE_SET_PREFIX);
}

const DAY = 86_400_000;
const ACTOR = 'fraud.mis@kkp-poc.local (u-demo)';

export function demoRuleSets(now: number): RuleSetView[] {
  const at = (daysAgo: number) => new Date(Math.floor(now / DAY) * DAY - daysAgo * DAY).toISOString();
  return [
    {
      id: 'demo-cnp-risk',
      name: 'Card not present (demo)',
      description: 'Online purchases: a second opinion beside the primary set.',
      enabled: true,
      primary: false,
      archived: false,
      createdUtc: at(9),
      createdBy: ACTOR,
      updatedUtc: at(2),
      updatedBy: ACTOR,
      version: '1.0.3',
      ruleCount: 3,
      enabledRuleCount: 3,
      setRuleCount: 1,
      lastRelease: { version: '1.0.3', savedUtc: at(2), actor: ACTOR, comment: 'two hits step up' },
      // R8b: demo sets are read-only whoever looks.
      access: 'view',
    },
    {
      id: 'demo-atm-night',
      name: 'ATM at night (demo, switched off)',
      enabled: false,
      primary: false,
      archived: false,
      createdUtc: at(5),
      createdBy: ACTOR,
      updatedUtc: at(5),
      updatedBy: ACTOR,
      version: '1.0.0',
      ruleCount: 0,
      enabledRuleCount: 0,
      setRuleCount: 0,
      lastRelease: { version: '1.0.0', savedUtc: at(5), actor: ACTOR, comment: 'rule set created' },
      access: 'view',
    },
  ];
}

/** The real list with the demo sets after it; read-only as a whole when the real one is. */
export function mergeRuleSets(real: RuleSetList | null, now: number): RuleSetList {
  const base = real ?? { source: 'proxy' as const, canManage: false, etag: '', sets: [] };
  return { ...base, sets: [...base.sets.filter((s) => !isDemoRuleSetId(s.id)), ...demoRuleSets(now)] };
}

/** A demo set's document: a few of the bundled rules under its own id, plus one set rule. */
export function demoRuleSetDocument(id: string, now: number = Date.now()): Promise<AdminRulesGetResponse> {
  const view = demoRuleSets(now).find((s) => s.id === id);
  const base = structuredClone(bundledRules as unknown as RuleSet);
  const empty = view?.ruleCount === 0;
  const ruleBased = empty ? [] : base.ruleBased.slice(0, 2);
  const patternBased = empty ? [] : base.patternBased.slice(0, 1);
  const ruleSet: RuleSet = {
    id: 'ruleset-active',
    ruleSetId: id,
    version: view?.version ?? '1.0.0',
    thresholds: base.thresholds,
    failSafe: base.failSafe,
    ruleBased,
    patternBased,
  };
  if (!empty) {
    ruleSet.setRules = [
      { id: 'SR-01', name: 'Two rules of this set on one transaction', enabled: true, minHits: 2, action: 'STEP_UP', points: 50 },
    ];
  }
  return Promise.resolve({ ruleSet, source: 'cosmos', loadedAt: new Date(now).toISOString() });
}

/** A demo set's history: its one release. */
export function demoRuleSetVersions(id: string, now: number = Date.now()): Promise<AdminRuleVersionsResponse> {
  const view = demoRuleSets(now).find((s) => s.id === id);
  const r = view?.lastRelease;
  return Promise.resolve({
    versions: r
      ? [{ version: r.version, savedUtc: r.savedUtc, actor: r.actor, ...(r.comment ? { comment: r.comment } : {}), ruleBasedEnabled: view.ruleCount > 0 ? 2 : 0, patternBasedEnabled: view.ruleCount > 0 ? 1 : 0, isActive: true }]
      : [],
  });
}
